/**
 * Shop branding → theme tokens.
 *
 * A shop picks four colours. Everything else the form needs — panel fills,
 * input backgrounds, secondary text — is derived from those by mixing the text
 * colour into the page colour, so a shop cannot produce an unreadable form by
 * choosing four colours that happen not to include a legible surface.
 *
 * Because the derived values are written onto the same CSS variables the
 * design system already uses, every component follows automatically; nothing
 * has to know branding exists.
 */

/** Only values we can safely interpolate into a CSS custom property. */
const SAFE_COLOUR = /^(#[0-9a-f]{3,8}|oklch\([\d\s.%/-]+\)|rgb\([\d\s,./%]+\))$/i;

function safe(value) {
  return typeof value === 'string' && SAFE_COLOUR.test(value.trim()) ? value.trim() : '';
}

/**
 * Map a branding record onto CSS variables.
 * Returns an object of custom property → value; empty when nothing is set.
 */
export function paletteFromBrand(branding) {
  if (!branding) return {};

  const accent = safe(branding.accent);
  const accentText = safe(branding.accentText);
  const background = safe(branding.background);
  const text = safe(branding.text);

  const vars = {};

  if (accent) {
    vars['--brand-accent'] = accent;
    vars['--primary'] = accent;
    vars['--ring'] = accent;
  }

  if (accentText) {
    vars['--brand-accent-foreground'] = accentText;
    vars['--primary-foreground'] = accentText;
  }

  if (background) {
    vars['--background'] = background;
    vars['--card'] = background;
    vars['--popover'] = background;
  }

  if (text) {
    vars['--foreground'] = text;
    vars['--card-foreground'] = text;
    vars['--popover-foreground'] = text;
  }

  // Surfaces: a hint of the text colour over the page. One rule covers light
  // and dark palettes alike, which a fixed grey cannot.
  if (background && text) {
    vars['--muted'] = `color-mix(in oklab, ${text} 6%, ${background})`;
    vars['--accent'] = `color-mix(in oklab, ${text} 8%, ${background})`;
    vars['--input'] = `color-mix(in oklab, ${text} 8%, ${background})`;
    vars['--secondary'] = `color-mix(in oklab, ${text} 10%, ${background})`;
    vars['--muted-foreground'] = `color-mix(in oklab, ${text} 62%, ${background})`;
    vars['--secondary-foreground'] = text;
  }

  return vars;
}

/**
 * Apply a palette to an element (usually documentElement).
 * Returns a cleanup function that removes exactly what it set.
 */
export function applyBranding(element, branding) {
  const palette = paletteFromBrand(branding);
  const keys = Object.keys(palette);

  keys.forEach((key) => element.style.setProperty(key, palette[key]));

  return () => keys.forEach((key) => element.style.removeProperty(key));
}
